import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Careers.css';
import Header from '../common/header/Header';
import { opportunitiesData } from '../data/OpportunitiesData';

const Careers = () => {
  const [filteredCareers, setFilteredCareers] = useState(opportunitiesData);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const isJobActive = (deadline) => new Date(deadline) >= new Date();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const getDaysRemaining = (deadline) => {
    const timeDiff = new Date(deadline).getTime() - new Date().getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24));
  };

  useEffect(() => {
    let filtered = opportunitiesData;
    if (filterType !== 'all') {
      filtered = filtered.filter(item => item.type.toLowerCase() === filterType.toLowerCase());
    }
    if (filterStatus !== 'all') {
      filtered = filtered.filter(item => {
        const active = isJobActive(item.applicationDeadline);
        return filterStatus === 'active' ? active : !active;
      });
    }
    setFilteredCareers(filtered);
  }, [filterType, filterStatus]);

  const totalConsultancies = opportunitiesData.filter(i => i.type === 'Consultancy').length;
  const totalCareers = opportunitiesData.filter(i => i.type === 'Career').length;

  return (
    <div className="careers-page-root">
      <Header />

      {/* ── HERO ── */}
      <section className="careers-hero">
        <div className="container">
          <div className="careers-hero-content">
            <div className="hero-eyebrow">Opportunities at FRT</div>
            <h1>
              Work With Us to <span>Transform Agriculture</span>
            </h1>
            <p>
              Farm Radio Trust connects expertise with purpose. Explore career openings and
              consultancy opportunities shaping the future of smallholder farming.
            </p>
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="hero-stat-number">{opportunitiesData.length}</span>
                <span className="hero-stat-label">Open Positions</span>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-number">{totalConsultancies}</span>
                <span className="hero-stat-label">Consultancies</span>
              </div>
              <div className="hero-stat">
                <span className="hero-stat-number">{totalCareers}</span>
                <span className="hero-stat-label">Full-time Roles</span>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-wave">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,48 C360,80 1080,0 1440,48 L1440,80 L0,80 Z" fill="#f5f7f4"/>
          </svg>
        </div>
      </section>

      {/* ── MAIN ── */}
      <section className="careers-main">
        <div className="container">

          {/* Filter toolbar */}
          <div className="filter-toolbar">
            <span className="filter-label">Filter by</span>
            <div className="filter-tabs">
              {[
                { label: 'All', value: 'all' },
                { label: 'Careers', value: 'career' },
                { label: 'Consultancies', value: 'consultancy' },
              ].map(tab => (
                <button
                  key={tab.value}
                  className={filterType === tab.value ? 'active' : ''}
                  onClick={() => setFilterType(tab.value)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="filter-divider" />
            <div className="filter-select-wrap">
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="inactive">Past Opportunities</option>
              </select>
            </div>
          </div>

          {/* Results count */}
          <div className="results-header">
            <p className="results-count">
              Showing <strong>{filteredCareers.length}</strong> of <strong>{opportunitiesData.length}</strong> opportunities
            </p>
          </div>

          {/* Cards grid */}
          <div className="opportunities-grid">
            {filteredCareers.length > 0 ? (
              filteredCareers.map(item => {
                const active = isJobActive(item.applicationDeadline);
                const daysLeft = getDaysRemaining(item.applicationDeadline);
                return (
                  <article key={item.id} className={`opp-card${!active ? ' expired' : ''}`}>
                    <div className="opp-card-inner">

                      <div className="opp-card-top">
                        <span className={`type-tag ${item.type.toLowerCase()}`}>
                          {item.type}
                        </span>
                        {active && daysLeft > 0 && daysLeft <= 14 && (
                          <span className="urgent-tag">Closing Soon</span>
                        )}
                      </div>

                      <div className="opp-card-body">
                        <p className="opp-category">{item.category}</p>
                        <h3 className="opp-title">{item.title}</h3>
                        <div className="opp-meta">
                          <div className="meta-row">
                            <i className="fas fa-map-marker-alt" />
                            <span>{item.location}</span>
                          </div>
                          <div className="meta-row">
                            <i className="fas fa-clock" />
                            <span>{item.duration}</span>
                          </div>
                        </div>
                        <p className="opp-excerpt">
                          {item.objectives || item.purpose || ''}
                        </p>
                      </div>

                      <div className="opp-card-footer">
                        <div className="deadline-block">
                          <span className="label">Deadline</span>
                          <span className={`date${!active ? ' expired-text' : ''}`}>
                            {formatDate(item.applicationDeadline)}
                          </span>
                        </div>
                        <Link to={`/careers/${item.id}`} className="btn-view-detail">
                          View Details <i className="fas fa-arrow-right" />
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })
            ) : (
              <div className="no-results">
                <div className="no-results-icon">
                  <i className="fas fa-search" />
                </div>
                <h3>No opportunities found</h3>
                <p>Try adjusting your filters or check back later for new postings.</p>
              </div>
            )}
          </div>

          {/* CTA */}
          <div className="careers-footer-cta">
            <div className="cta-card">
              <div className="cta-text">
                <h2>Don't see the right opportunity?</h2>
                <p>
                  Register your interest and we'll reach out when a relevant role or
                  consultancy comes up. We'd love to hear from you.
                </p>
              </div>
              <a href="mailto:info@farmradiomw.org" className="btn-cta-primary">
                Register Interest <i className="fas fa-envelope" />
              </a>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
};

export default Careers;
